import type { EntityType, ErpGraph } from '@/features/erp/contract'
import { ENTITY_TYPES } from '@/features/erp/contract'
import type { AdminRole } from '@/features/admin/shared/permissions'
import { ERP_VIEWS, EXPERIENCE_TREES, experienceOfArea, experienceOfView, experienceTreeTabs, treeNodeAllowed, type ErpView, type ExperienceKey, type NavEntry, type XpTreeNode } from '@/features/erp/frentes'
import type { ConfigSection } from '@/features/erp/config/registry'
import { ENTITY_META } from '@/features/erp/ui/entity'

/* ERP shell · pure logic of the Staff navigation: the frente menu (one item per experience), the Configurações
   groups, and the trail (path walked between screens and fichas, max 8, kept in the session). */

/** One item of the Staff menu: an area of the frente menu, i.e. an experience (opens its first screen, the experience
    overview; its screens become tabs, or a tree in the sidebar when the experience has one). `xp` = the experience (null for an area outside the catalog, e.g. "Mais"). */
export type StaffMenuItem = { key: string; label: string; to: string; views: ErpView[]; xp: ExperienceKey | null }

/** Loose screens (Início, still in old custom menus) stay out of the menu; their routes keep working. */
export function staffMenu(nav: readonly NavEntry[]): StaffMenuItem[] {
    const out: StaffMenuItem[] = []
    for (const e of nav) {
        if (!e.area) continue
        const hit = out.find(x => x.label === e.area)
        if (hit) hit.views.push(e.view)
        else out.push({ key: `area:${e.area}`, label: e.area, to: e.view.path, views: [e.view], xp: experienceOfArea(e.area)?.key ?? null })
    }
    return out
}

export const isUnder = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`)

/** Is this menu item the current one? An experience is current when the screen is one of its tabs. */
export function menuItemActive(item: StaffMenuItem, pathname: string): boolean {
    return item.views.some(v => isUnder(pathname, v.path))
}

// ---------- experience tree (sidebar) ----------

/** A node of an experience tree as the person sees it. `id` = path of labels (stable key for the open state);
    `view` = its own screen when the frente menu has it (null = plain expander); `tab` = a tab of that screen (null = its
    first tab); `to` = its screen (with `?tab=` for a tab node) or, without one, the first visible descendant's. */
export type XpTreeItem = { id: string; label: string; view: ErpView | null; tab: string | null; to: string; children: XpTreeItem[] }

/** Does this experience swap the top tabs for a tree in the sidebar? */
export const hasXpTree = (xp: ExperienceKey | null | undefined): boolean => !!xp && !!EXPERIENCE_TREES[xp]

/** Same, by menu area name (AppShell gets the area from areaOfPath). */
export const areaHasTree = (area: string): boolean => hasXpTree(experienceOfArea(area)?.key)

/** Does the tree of this experience carry the tabs of its overview (Staff: Meu dia, Minha jornada…)? Then the overview
    hides its own tab row where the tree shows. */
export const treeHasTabs = (xp: ExperienceKey): boolean => experienceTreeTabs(xp).length > 0

/** Address of a tree node: the screen, plus `?tab=` for a tab node. */
export const treeNodeTo = (path: string, tab: string | null | undefined): string => (tab ? `${path}?tab=${encodeURIComponent(tab)}` : path)

/** Tree of an experience filtered by the screens the person has in it (`views` = the menu item's screens, already
    filtered by permissions and Configurações) and by the profile (`role`, for nodes with `roles`: Staff › Equipe only for
    leads). A node stays when its screen is allowed or any descendant stays. Screens in `views` not placed in the tree
    are appended at the root end (nothing disappears), except the overview itself. null = no tree. */
export function experienceTree(xp: ExperienceKey, views: readonly ErpView[], role: AdminRole | null = null): XpTreeItem[] | null {
    const def = EXPERIENCE_TREES[xp]
    if (!def) return null
    const byKey = new Map(views.map(v => [v.key, v]))
    const placed = new Set<string>()
    const walk = (nodes: readonly XpTreeNode[], parent: string): XpTreeItem[] => {
        const out: XpTreeItem[] = []
        for (const node of nodes) {
            const id = `${parent}/${node.label}`
            if (node.view) placed.add(node.view)
            if (!treeNodeAllowed(node, role)) continue
            const view = (node.view && byKey.get(node.view)) || null
            const tab = view && node.tab ? node.tab : null
            const children = walk(node.children ?? [], id)
            const to = view ? treeNodeTo(view.path, tab) : children[0]?.to
            if (to) out.push({ id, label: node.label, view, tab, to, children })
        }
        return out
    }
    const tree = walk(def, xp)
    // The overview (/staff/xp/<key>) is no tree node outside Staff: it opens only by its address, never appended here
    placed.add(`xp-${xp}`)
    for (const v of views) if (!placed.has(v.key)) tree.push({ id: `${xp}/+${v.key}`, label: v.label, view: v, tab: null, to: v.path, children: [] })
    return tree
}

/** Screens of a node and all its descendants (for the badge sum; tab nodes carry no badge of their own). */
export function treeViews(item: XpTreeItem): ErpView[] {
    return [...(item.view && !item.tab ? [item.view] : []), ...item.children.flatMap(treeViews)]
}

/** Ids from the root to the node of the current screen (longest path match), [] when none. The last one is the active
    node; the others are the ancestors that open by themselves. `tab` = the current ?tab=: a tab node is current only on
    its screen with that tab (and beats the plain node of the same screen); without ?tab, or with a tab that has no node
    here, the plain node of the screen is the current one (xp-staff without ?tab = Meu dia). */
export function treeActivePath(tree: readonly XpTreeItem[], pathname: string, tab: string | null = null): string[] {
    const best = { ids: [] as string[], score: -1 }
    const walk = (nodes: readonly XpTreeItem[], ids: string[]) => {
        for (const node of nodes) {
            const here = [...ids, node.id]
            const v = node.view
            const hit = !!v && (node.tab ? pathname === v.path && node.tab === tab : isUnder(pathname, v.path))
            const score = v ? v.path.length + (node.tab ? 0.5 : 0) : -1
            if (hit && score > best.score) Object.assign(best, { ids: here, score })
            walk(node.children, here)
        }
    }
    walk(tree, [])
    return best.ids
}

export type ConfigGroup = { label: string; items: { to: string; label: string; key: string }[] }

const CONFIG_GROUP_ORDER: readonly ConfigSection['group'][] = ['Pessoas', 'ERP', 'Segurança', 'Organização']

/** ERP settings sections grouped in the fixed order (empty groups left out). */
export function configGroups(sections: readonly Pick<ConfigSection, 'key' | 'label' | 'group'>[]): ConfigGroup[] {
    return CONFIG_GROUP_ORDER
        .map(label => ({ label, items: sections.filter(s => s.group === label).map(s => ({ key: s.key, label: s.label, to: `/configuracoes/${s.key}` })) }))
        .filter(g => g.items.length > 0)
}

/** Paths of the Configurações space (the old Admin space + /configuracoes): only the admin profile opens them. */
export function isConfigPath(pathname: string): boolean {
    return !isUnder(pathname, '/staff')
}

// ---------- trail ----------

export type TrailStep = { path: string }
export const TRAIL_MAX = 8

/** Walks to `path`: back to it if it is already on the trail (cut), otherwise appended. `fresh` (menu, tabs, frente)
    restarts the trail at this screen. At most TRAIL_MAX steps (the oldest drop). */
export function walkTrail(trail: readonly TrailStep[], path: string, fresh = false): TrailStep[] {
    if (fresh) return [{ path }]
    const at = trail.findIndex(s => s.path === path)
    if (at >= 0) return trail.slice(0, at + 1)
    return [...trail, { path }].slice(-TRAIL_MAX)
}

/** Parses what came from sessionStorage (anything odd = empty trail). */
export function parseTrail(raw: string | null): TrailStep[] {
    try {
        const v: unknown = raw ? JSON.parse(raw) : null
        if (!Array.isArray(v)) return []
        return v
            .filter((s): s is TrailStep => !!s && typeof s === 'object' && typeof (s as TrailStep).path === 'string' && (s as TrailStep).path.startsWith('/staff'))
            .slice(-TRAIL_MAX)
    } catch {
        return []
    }
}

/** Catalog screen of a path (longest match, so /staff/igrejas/regua beats /staff/igrejas). */
export function viewOfPath(pathname: string): ErpView | null {
    let best: ErpView | null = null
    for (const v of Object.values(ERP_VIEWS)) if (isUnder(pathname, v.path) && (!best || v.path.length > best.path.length)) best = v
    return best
}

/** Experience of a screen (for the trail: "Ambassadors · Visão geral"). */
function defaultAreaOf(key: string): string | null {
    return experienceOfView(key)?.label ?? null
}

const AMBIGUOUS = /^(Visão|Motor de busca|Régua|Não aprovad|Busca no mapa)/

/** Name of a screen in the trail; generic names take the area along. */
export function viewTrailLabel(view: ErpView, nav: readonly NavEntry[]): string {
    if (!AMBIGUOUS.test(view.label)) return view.label
    const area = nav.find(e => e.view.key === view.key)?.area || defaultAreaOf(view.key)
    return area ? `${area} · ${view.label}` : view.label
}

/** Staff screens outside the catalog (still reachable by address). */
const OTHER_STAFF: Record<string, string> = { '/staff/hoje': 'Hoje', '/staff/equipe': 'Equipe', '/staff/denuncia': 'Denúncia' }

export type FichaRef = { type: EntityType; id: string }

export function fichaOfPath(pathname: string): FichaRef | null {
    const m = /^\/staff\/ficha\/([^/]+)\/([^/]+)\/?$/.exec(pathname)
    if (!m || !(ENTITY_TYPES as readonly string[]).includes(m[1]!)) return null
    try {
        return { type: m[1] as EntityType, id: decodeURIComponent(m[2]!) }
    } catch {
        return null
    }
}

export type TrailItem = { path: string; label: string; type: EntityType | null }

/** Label of each step: screen name, entity name (from the graph when loaded) or the entity kind. */
export function trailItems(trail: readonly TrailStep[], nav: readonly NavEntry[], graph: Pick<ErpGraph, EntityType> | null | undefined): TrailItem[] {
    return trail.map(({ path }) => {
        const ficha = fichaOfPath(path)
        if (ficha) {
            const e = graph?.[ficha.type].find(x => x.id === ficha.id)
            return { path, type: ficha.type, label: e?.name ?? ENTITY_META[ficha.type].label }
        }
        const view = viewOfPath(path)
        if (view) return { path, type: null, label: viewTrailLabel(view, nav) }
        return { path, type: null, label: OTHER_STAFF[path] ?? 'Tela' }
    })
}
